import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  CrewBriefing,
  CrewBriefingUpsertRequest,
  FileStreamResult,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getApiErrorStatus } from '@/util/error-utils.ts';
import {
  createFileDownloadResponseHandlerOption,
  downloadFile,
} from '@/util/file-download.ts';

export const useCharterCrewBriefingDetailStore = defineStore('charter-crew-briefing-detail', () => {
  const api = useApiClient();

  const briefing = ref<CrewBriefing | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const downloading = ref(false);

  const isLoading = computed(() => loading.value);
  const isSaving = computed(() => saving.value);
  const isDownloading = computed(() => downloading.value);

  async function load(charterId: number): Promise<CrewBriefing | null> {
    loading.value = true;
    briefing.value = null;

    try {
      const result = await api.charter.byCharterId(charterId).crewBriefing.get();
      briefing.value = result ?? null;
      return briefing.value;
    } catch (error) {
      if (getApiErrorStatus(error) === 404) {
        return null;
      }

      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function save(
    charterId: number,
    request: CrewBriefingUpsertRequest
  ): Promise<CrewBriefing> {
    saving.value = true;

    try {
      const result = await api.charter.byCharterId(charterId).crewBriefing.put(request);
      if (!result) {
        throw new Error('Crew briefing could not be saved.');
      }

      briefing.value = result;
      return result;
    } finally {
      saving.value = false;
    }
  }

  async function downloadPdf(
    charterId: number,
    templateFileName: string
  ): Promise<FileStreamResult> {
    downloading.value = true;

    try {
      const file = await api.charter.byCharterId(charterId).crewBriefing.pdf.get({
        queryParameters: { templateFileName },
        options: [createFileDownloadResponseHandlerOption()],
      }) as unknown as FileStreamResult | undefined;

      if (!file?.fileStream) {
        throw new Error('Crew briefing PDF could not be downloaded.');
      }

      const downloadedFile = downloadFile(file, `CrewBriefing_${charterId}.pdf`);
      if (!downloadedFile) {
        throw new Error('Crew briefing PDF could not be downloaded.');
      }

      return downloadedFile;
    } finally {
      downloading.value = false;
    }
  }

  function reset(): void {
    briefing.value = null;
    loading.value = false;
    saving.value = false;
    downloading.value = false;
  }

  return {
    briefing,
    isLoading,
    isSaving,
    isDownloading,
    load,
    save,
    downloadPdf,
    reset,
  };
});
